// Shared tokens, formatters and status maps for the partner portal.
//
// All data now comes from real backend endpoints:
//   GET /user-mgmt/partner-portal/dashboard
//   GET /user-mgmt/partner-portal/customers
//   GET /user-mgmt/partner-portal/customers/{id}/invoices
//   GET /user-mgmt/partner-portal/commission

// FundTap "Trust / Momentum" design system palette (nav redesign v2).
// Retired values (#0A1F3D, #6FD89F, #3FB97A, #80E8B3, #34C77B, …) must not be used.
export const FT = {
  navy: "#00213D", // Trust navy
  navyDeep: "#1B3A52", // Trust Light (hover)
  navy80: "#475569", // body text
  navy60: "#6B7280", // muted text
  navy40: "#94A3B8", // dim text
  navy15: "#E5E7EB", // default border
  navy08: "#ECEFF3", // subtle border
  navy04: "#F4F6F9", // soft grey surface
  mint: "#76E0A9", // Momentum green (accents / active customer toggle)
  mintDeep: "#00213D", // emphasis/text on/near green is navy (system never uses white-on-green)
  mintBg: "#E0F8E8", // Momentum surface
  danger: "#D04A4A", // validation border / required marker
  dangerDeep: "#9B2C2C", // error text
  dangerBg: "#FBE6E6", // error/warning banner surface
  dangerBorder: "#F5C2C2", // error/warning banner border
  paper: "#FFFFFF",
  paperWarm: "#FAFAFC", // page background
  font: "'DM Sans', system-ui, sans-serif"
};

// The partner's display name, cached from the dashboard response so the header
// avatar shows the right initials immediately on every tab mount instead of
// flashing a placeholder. userLogout() runs localStorage.clear(), so this is
// cleared on sign-out along with the rest of the session.
const PARTNER_NAME_KEY = "ftPartnerName";

export function persistPartnerName(name) {
  try {
    if (name) localStorage.setItem(PARTNER_NAME_KEY, name);
  } catch {
    /* localStorage unavailable — the avatar just falls back to a neutral icon */
  }
}

/**
 * Forget the cached name. Call this when a partner session ends without a full
 * logout — staff reverting from an impersonated partner back to their own
 * account — so the next partner they impersonate doesn't briefly show the
 * previous one's initials.
 */
export function clearPersistedPartnerName() {
  try {
    localStorage.removeItem(PARTNER_NAME_KEY);
  } catch {
    /* localStorage unavailable — nothing was stored to clear */
  }
}

export function getPersistedPartnerName() {
  try {
    return localStorage.getItem(PARTNER_NAME_KEY) || "";
  } catch {
    return "";
  }
}

export const STAGES = {
  approved: { fg: "#00213D", bg: "#E0F8E8", dot: "#76E0A9", label: "Approved" },
  pending: { fg: "#92400E", bg: "#FDF4E6", dot: "#D9A441", label: "Pending" },
  customer_dd: {
    fg: "#1B3A52",
    bg: "#F4F6F9",
    dot: "#94A3B8",
    label: "Customer DD"
  },
  not_approved: {
    fg: "#991B1B",
    bg: "#FBE6E6",
    dot: "#C0564E",
    label: "Not approved"
  },
  archived: { fg: "#6B7280", bg: "#ECEFF3", dot: "#94A3B8", label: "Archived" }
};

export const INVOICE_STATUSES = {
  pending: { label: "Pending", fg: "#92400E", bg: "#FDF4E6", dot: "#D9A441" },
  funded: { label: "Funded", fg: "#1B3A52", bg: "#F4F6F9", dot: "#94A3B8" },
  completed: {
    label: "Completed",
    fg: "#00213D",
    bg: "#E0F8E8",
    dot: "#76E0A9"
  },
  overdue: { label: "Overdue", fg: "#991B1B", bg: "#FBE6E6", dot: "#C0564E" },
  customer_not_required: {
    label: "Customer Not Required",
    fg: "#6B7280",
    bg: "#ECEFF3",
    dot: "#94A3B8"
  },
  not_approved: {
    label: "Not Approved",
    fg: "#991B1B",
    bg: "#F5D6D6",
    dot: "#C0564E"
  }
};

// Map backend user_status_id (returned as a string) to the 5 design stages.
// Mirrors the backend PartnerStageMapper.
export function statusIdToStage(id) {
  const n = Number(id);
  if (n === 2 || n === 9) return "approved";
  if (n === 1) return "pending";
  if (n === 13) return "customer_dd";
  if (n === 3 || n === 8) return "not_approved";
  if ([4, 5, 6, 7, 10, 11, 12].includes(n)) return "archived";
  return "pending";
}

export const fmt = {
  money: (n, frac = 2) =>
    "$" +
    (n ?? 0).toLocaleString("en-NZ", {
      minimumFractionDigits: frac,
      maximumFractionDigits: frac
    }),
  money0: (n) => "$" + Math.round(n ?? 0).toLocaleString("en-NZ"),
  date: (s) => {
    if (!s) return "—";
    return new Date(s).toLocaleDateString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  },
  dateShort: (s) => {
    if (!s) return "—";
    return new Date(s).toLocaleDateString("en-NZ", {
      day: "2-digit",
      month: "short"
    });
  }
};
